import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { useCurrency } from "@/context/CurrencyContext";
import {
  MapPin, Users, Coins, Bus, Star, ExternalLink, GraduationCap,
  Coffee, Library, Dumbbell, Beer, Utensils, Laptop, ArrowLeft, Quote,
  Home, Heart, Sparkles, Compass, MessageCircle, Calculator, Wallet,
  Share2, Check, ShieldCheck, ClipboardList, CloudSun, Church, Soup,
  Plane, Briefcase, FileCheck, CalendarDays, Stethoscope, AlertTriangle,
  Rocket, DollarSign, Scale, Cpu, Wrench, Palette, Landmark, CreditCard,
  FlaskConical, Leaf, Gamepad2, ShoppingCart, Car, Wheat, BookOpen,
  Train, Footprints, Bike, Moon, TreeDeciduous, Mountain, Waves,
  Music, PartyPopper, Trophy, Pin, PinOff,
} from "lucide-react";
import { toast } from "sonner";
import { useCompareShortlist } from "@/hooks/useCompareShortlist";
import { useCityShortlist } from "@/hooks/useCityShortlist";
import PlaceReadinessCard from "@/components/PlaceReadinessCard";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Legend, Tooltip,
} from "recharts";
import SEOHead from "@/components/app/SEOHead";
import { vintageLabelFor, isVintageStale } from "@/lib/vintage";
import { CreativeProvenanceChip } from "@/components/CreativeProvenanceChip";
import { useJourney } from "@/context/JourneyContext";
import {
  SafetySection,
  ArrivalChecklistSection,
  WeatherSection,
  FaithSection,
  FoodFromHomeSection,
  GettingHomeSection,
  PartTimeWorkSection,
  PostStudyVisaSection,
  AnnualEventsSection,
  WellbeingSection,
} from "./city_detail/CityDetailExtras";

const CATEGORY_META = {
  cafe:      { label: "Study cafés",   icon: Coffee   },
  library:   { label: "Libraries",     icon: Library  },
  gym:       { label: "Gyms",          icon: Dumbbell },
  nightlife: { label: "Nightlife",     icon: Beer     },
  food:      { label: "Cheap eats",    icon: Utensils },
  coworking: { label: "Coworking",     icon: Laptop   },
};

const currencySymbol = (c) => ({
  GBP: "£", USD: "$", EUR: "€", CAD: "C$", AUD: "A$",
  NZD: "NZ$", SGD: "S$", HKD: "HK$", CHF: "CHF ", DKK: "kr",
  JPY: "¥", KRW: "₩", CNY: "¥", INR: "₹", AED: "AED ", CZK: "Kč", SEK: "kr",
}[c] || "");

// Human labels for research-score keys (CSV-derived).
const CAREER_LABELS = {
  research: "University research", startups: "Startup ecosystem", finance: "Finance",
  law: "Law", tech: "Software & AI", healthcare: "Healthcare",
  engineering: "Engineering / hardware", creative: "Creative & media",
  government: "Government / policy",
  fintech: "Fintech", biotech: "Health & biotech", aerospace: "Aerospace / aviation",
  cleantech: "Energy & cleantech", gaming: "Gaming & leisure media",
  ecommerce: "Ecommerce & retail", mobility: "Mobility & logistics",
  agritech: "Food & agriculture", edtech: "Education",
  publishing: "Publishing", content_creation: "Digital content", agencies: "Creative agencies",
};
const CAREER_ICONS = {
  research: GraduationCap, startups: Rocket, finance: DollarSign,
  law: Scale, tech: Cpu, healthcare: Stethoscope,
  engineering: Wrench, creative: Palette, government: Landmark,
  fintech: CreditCard, biotech: FlaskConical, aerospace: Plane,
  cleantech: Leaf, gaming: Gamepad2, ecommerce: ShoppingCart,
  mobility: Car, agritech: Wheat, edtech: BookOpen,
  publishing: BookOpen, content_creation: Music, agencies: Palette,
};
const TRANSPORT_LABELS = {
  rail: "Public transport", air: "Air connectivity", walk: "Walkability",
  cycle: "Cycling", night: "Night transport",
};
const TRANSPORT_ICONS = {
  rail: Train, air: Plane, walk: Footprints, cycle: Bike, night: Moon,
};
const LIFESTYLE_LABELS = {
  arts: "Arts & Culture", museums: "Museums", food: "Food Scene",
  green: "Green Spaces", nightlife: "Nightlife",
  sports_participate: "Sports (participate)", sports_watch: "Sports (watch)",
  outdoors: "Outdoors", beach: "Beaches", diverse: "Diverse Community",
  live_music: "Live Music", cafe: "Cafe Culture", festivals: "Festivals",
};
const LIFESTYLE_SHORT = {
  arts: "Arts", museums: "Museums", food: "Food",
  green: "Green", nightlife: "Nightlife",
  sports_participate: "Sport (do)", sports_watch: "Sport (watch)",
  outdoors: "Outdoors", beach: "Beach", diverse: "Diverse",
  live_music: "Music", cafe: "Cafe", festivals: "Festivals",
};
const LIFESTYLE_ICONS = {
  arts: Palette, museums: Landmark, food: Utensils, green: TreeDeciduous,
  nightlife: Beer, sports_participate: Dumbbell, sports_watch: Trophy,
  outdoors: Mountain, beach: Waves, diverse: Users,
  live_music: Music, cafe: Coffee, festivals: PartyPopper,
};
// Canonical order for the radar (13 axes, thematic clusters):
// Arts&scene → Active&outdoors → City-life.
const LIFESTYLE_ORDER = [
  "arts", "museums", "live_music", "festivals", "cafe", "food",
  "sports_participate", "outdoors", "beach", "green",
  "nightlife", "sports_watch", "diverse",
];

export default function CityDetail() {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { profile: journeyProfile } = useJourney();
  const { has: hasCity, toggle: toggleCity, slugs: cityShortlistSlugs, full: cityShortlistFull, cap: cityShortlistCap } = useCityShortlist();
  // Session-scoped dismissal of the floating compare nudge — students
  // who close it once shouldn't see it again for the rest of the visit.
  const [nudgeDismissed, setNudgeDismissed] = useState(() => {
    try { return sessionStorage.getItem("wua:cityCompareNudge:dismissed") === "1"; }
    catch { return false; }
  });
  const dismissNudge = () => {
    try { sessionStorage.setItem("wua:cityCompareNudge:dismissed", "1"); } catch {}
    setNudgeDismissed(true);
  };

  // Subject-coverage badge — surface a per-subject pill on the hero
  // when the visitor arrived from confirmed PICK subjects. Filters
  // out defensive rank-labels ("Leading match" etc). One lightweight
  // fetch cached backend-side for 15 min.
  const RANK_LABEL_BLOCKLIST = new Set([
    "leading match", "close match", "also worth exploring",
  ]);
  const pickedSubjects = ((journeyProfile?.what?.subjects_confirmed) || [])
    .map((s) => (typeof s === "string" ? s : (s?.label || s?.slug)))
    .filter(Boolean)
    .filter((v) => !RANK_LABEL_BLOCKLIST.has(String(v).toLowerCase().trim()));
  const [subjectCountsHere, setSubjectCountsHere] = useState(null); // { subject: count }
  useEffect(() => {
    if (!pickedSubjects.length || !slug) { setSubjectCountsHere(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/cities/subject-coverage", {
          params: { subject: pickedSubjects },
          paramsSerializer: { indexes: null },
        });
        if (!cancelled) {
          const per = data?.per_subject || {};
          const out = {};
          for (const subj of pickedSubjects) {
            out[subj] = per?.[subj]?.[slug]?.count || 0;
          }
          setSubjectCountsHere(out);
        }
      } catch {
        if (!cancelled) setSubjectCountsHere(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedSubjects.join("|"), slug]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get(`/cities/${slug}`);
        if (!cancelled) setCity(res.data || null);
      } catch (e) {
        if (!cancelled) setError(e.response?.status === 404 ? "not_found" : (e.message || "Failed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF8F3] max-w-7xl mx-auto px-6 py-16" data-testid="city-loading">
        <div className="h-8 w-32 bg-stone-200 rounded mb-6 animate-pulse" />
        <div className="h-16 w-96 bg-stone-200 rounded mb-4 animate-pulse" />
        <div className="h-32 bg-stone-100 rounded animate-pulse" />
      </div>
    );
  }
  if (error === "not_found" || !city) {
    return (
      <div className="min-h-screen bg-[#FBF8F3] max-w-3xl mx-auto px-6 py-16" data-testid="city-not-found">
        <h1 className="font-editorial text-4xl mb-3">City not found</h1>
        <p className="text-stone-600 mb-6">We haven't published a guide for this city yet.</p>
        <Link to="/cities">
          <Button variant="outline"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to city guides</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid={`city-detail-${city.slug}`}>
      <SEOHead
        title={`${city.name} student guide — WhichUniversityAdvisor`}
        description={city.editorial?.vibe?.slice(0, 160)}
      />

      <CityTOC city={city} />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-8">
        <Link
          to="/cities"
          className="text-xs uppercase tracking-wider text-stone-500 hover:text-[#002147] inline-flex items-center gap-1.5"
          data-testid="city-back-link"
        >
          <ArrowLeft className="h-3 w-3" /> All city guides
        </Link>
        <div className="mt-4 flex items-center gap-2 text-stone-500 text-sm">
          <MapPin className="h-4 w-4" /> {city.country}
        </div>
        <h1 className="font-editorial text-5xl sm:text-6xl mt-2 tracking-tight" data-testid="city-name">
          {city.name}
        </h1>
        {/* Subject-coverage badge — appears only when the visitor
            arrived from confirmed PICK subjects. Green if any of the
            picked subjects have a university in this city (lists each
            with its count); muted amber if none do. */}
        {pickedSubjects.length > 0 && subjectCountsHere && (() => {
          const offered = pickedSubjects.filter((s) => (subjectCountsHere[s] || 0) > 0);
          const missing = pickedSubjects.filter((s) => (subjectCountsHere[s] || 0) === 0);
          const hasAny = offered.length > 0;
          return (
            <div
              data-testid="city-subject-coverage-badge"
              className={[
                "mt-3 inline-flex flex-wrap items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium max-w-full",
                hasAny
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-amber-50 border-amber-200 text-amber-900",
              ].join(" ")}
            >
              <span aria-hidden="true">{hasAny ? "✓" : "—"}</span>
              {hasAny ? (
                <>
                  {offered.map((subj, i) => (
                    <span key={subj}>
                      {subj}: {subjectCountsHere[subj]} universit
                      {subjectCountsHere[subj] === 1 ? "y" : "ies"} here
                      {i < offered.length - 1 ? "," : ""}
                    </span>
                  ))}
                  {missing.length > 0 && (
                    <span className="text-emerald-700/60">
                      · No {missing.join(", ")} here
                    </span>
                  )}
                </>
              ) : (
                <span>No {pickedSubjects.join(", ")} at universities in {city.name}</span>
              )}
            </div>
          );
        })()}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl">
          <Stat label="Population" value={city.population ? `${(city.population/1e6).toFixed(2)}M` : "—"} />
          <Stat label="Students" value={city.student_population ? `${Math.round(city.student_population/1000)}k` : "—"} />
          <Stat
            label="Monthly budget"
            value={
              city.cost_of_living?.student_budget_low
                ? `${currencySymbol(city.cost_of_living.currency)}${city.cost_of_living.student_budget_low.toLocaleString()}+`
                : "—"
            }
          />
          <Stat label="Cost data" value={city.cost_of_living?.as_of || "—"} />
        </div>

        {/* Place-pillar shortlist — add-to-journey CTA and readiness
            readout. Mirrors the Apply pillar's shortlist gate: gives
            the student one clear next step from the city page. */}
        <div className="mt-6 flex flex-col gap-3 max-w-3xl">
          <div className="flex flex-wrap items-center gap-3">
            {(() => {
              const added = hasCity(city.slug);
              const disabled = !added && cityShortlistFull;
              const onClick = () => {
                if (disabled) {
                  toast.error(`You can shortlist up to ${cityShortlistCap} cities.`);
                  return;
                }
                toggleCity(city.slug);
                toast.success(added
                  ? `${city.name} removed from your Place shortlist.`
                  : `${city.name} added to your Place shortlist.`);
              };
              return (
                <Button
                  type="button"
                  onClick={onClick}
                  disabled={disabled}
                  data-testid="city-add-to-shortlist"
                  className={added
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "bg-[#4C1D95] hover:bg-[#3B0F72] text-white"}
                >
                  {added ? (
                    <><Check className="h-4 w-4 mr-1.5" /> Added to Place shortlist</>
                  ) : (
                    <><Pin className="h-4 w-4 mr-1.5" /> Add {city.name} to Place shortlist</>
                  )}
                </Button>
              );
            })()}
            {cityShortlistSlugs.length >= 2 && (
              <Link
                to={`/cities/compare?slugs=${cityShortlistSlugs.join(",")}`}
                data-testid="city-add-open-compare"
                className="inline-flex items-center gap-1 text-xs text-[#4C1D95] hover:text-[#002147] underline underline-offset-2"
              >
                Compare all {cityShortlistSlugs.length} →
              </Link>
            )}
            {nudgeDismissed && cityShortlistSlugs.length >= 2 && (
              <button
                type="button"
                onClick={() => {
                  try { sessionStorage.removeItem("wua:cityCompareNudge:dismissed"); } catch {}
                  setNudgeDismissed(false);
                  toast.success("Compare tray restored.");
                }}
                data-testid="city-compare-nudge-restore"
                className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white text-stone-600 hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors px-2.5 py-0.5 text-[11px] font-medium"
                title="Show the floating compare tray again"
              >
                Show compare tray
              </button>
            )}
          </div>
          <PlaceReadinessCard testId="city-place-readiness" />
        </div>
      </section>

      {/* VIBE + BEST FOR + WATCH OUT */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white border border-stone-200 rounded-lg p-6 lg:p-8">
          <h2 className="font-editorial text-2xl mb-4">The vibe</h2>
          <p className="text-stone-700 leading-relaxed whitespace-pre-line" data-testid="city-vibe">
            {city.editorial?.vibe}
          </p>

          {city.editorial?.getting_around && (
            <div className="mt-6 pt-6 border-t border-stone-100">
              <div className="flex items-center gap-2 mb-2">
                <Bus className="h-4 w-4 text-[#002147]" />
                <h3 className="font-semibold text-sm uppercase tracking-wider">Getting around</h3>
              </div>
              <p className="text-sm text-stone-700 leading-relaxed">
                {city.editorial.getting_around}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {city.editorial?.best_for?.length > 0 && (
            <div className="bg-white border border-stone-200 rounded-lg p-5" data-testid="city-best-for">
              <h3 className="font-editorial text-lg mb-3">Best for</h3>
              <ul className="space-y-2">
                {city.editorial.best_for.map((b, i) => (
                  <li key={i} className="text-sm text-stone-700 flex items-start gap-2">
                    <Star className="h-3.5 w-3.5 text-[#4C1D95] mt-1 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {city.editorial?.watch_out?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-5" data-testid="city-watch-out">
              <h3 className="font-editorial text-lg mb-3">Watch out for</h3>
              <ul className="space-y-2">
                {city.editorial.watch_out.map((b, i) => (
                  <li key={i} className="text-sm text-amber-900 flex items-start gap-2">
                    <span className="text-amber-600 font-bold mt-0.5">·</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* COST OF LIVING */}
      {city.cost_of_living && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10">
          <div className="flex items-baseline justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="font-editorial text-2xl">Cost of living</h2>
              {/* Provenance chip — mirrors the "Verified Jul 2026"
                  chip on curated firm salary bands so families see the
                  same trust signal on rent and food benchmarks that
                  they now see on pay bands. Chip is skipped entirely
                  when the backend doesn't ship an `as_of`, so we never
                  hallucinate a vintage. */}
              {city.cost_of_living.as_of && (() => {
                const label = vintageLabelFor(city.cost_of_living.as_of);
                const stale = isVintageStale(city.cost_of_living.as_of);
                return (
                  <span
                    data-testid="city-cost-vintage-chip"
                    title={
                      stale
                        ? `Rent + food figures below were sampled in ${label} and are overdue for our 180-day refresh — treat them as directional.`
                        : `Rent + food figures below were sampled in ${label} from ${city.cost_of_living.source_url ? "Numbeo" : "verified sources"}.`
                    }
                    className={
                      "inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider rounded-full border px-2 py-0.5 " +
                      (stale
                        ? "border-amber-400 bg-amber-50 text-amber-900"
                        : "border-emerald-400 bg-white text-emerald-800")
                    }
                  >
                    <ShieldCheck className="h-2.5 w-2.5" aria-hidden />
                    {stale ? `Refresh due · Verified ${label}` : `Verified ${label}`}
                  </span>
                );
              })()}
            </div>
            <a
              href={city.cost_of_living.source_url}
              target="_blank" rel="noreferrer noopener"
              className="text-xs text-stone-500 hover:text-[#002147] inline-flex items-center gap-1"
              data-testid="city-cost-source"
            >
              Source: Numbeo <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <div className="bg-white border border-stone-200 rounded-lg p-6 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="city-cost-grid">
            <CostRow label="Rent (1BR, centre)"  amount={city.cost_of_living.rent_1br_center}  cur={city.cost_of_living.currency} />
            <CostRow label="Rent (1BR, outside)" amount={city.cost_of_living.rent_1br_outside} cur={city.cost_of_living.currency} />
            <CostRow label="Student accommodation" amount={city.cost_of_living.student_accommodation} cur={city.cost_of_living.currency} />
            <CostRow label="Utilities / month"    amount={city.cost_of_living.utilities_monthly}   cur={city.cost_of_living.currency} />
            <CostRow label="Internet / month"     amount={city.cost_of_living.internet_monthly}    cur={city.cost_of_living.currency} />
            <CostRow label="Mobile phone / month" amount={city.cost_of_living.mobile_monthly}      cur={city.cost_of_living.currency} />
            <CostRow label="Weekly shop"           amount={city.cost_of_living.weekly_shop}         cur={city.cost_of_living.currency} />
            <CostRow label="Groceries / month"    amount={city.cost_of_living.groceries_monthly} cur={city.cost_of_living.currency} />
            <CostRow label="Monthly transport"   amount={city.cost_of_living.monthly_transport} cur={city.cost_of_living.currency} />
            <CostRow label="Pint of beer"         amount={city.cost_of_living.beer_pint}         cur={city.cost_of_living.currency} />
            <CostRow label="Coffee"                amount={city.cost_of_living.coffee}             cur={city.cost_of_living.currency} />
            <CostRow label="Cinema ticket"        amount={city.cost_of_living.cinema_ticket}      cur={city.cost_of_living.currency} />
            <CostRow label="Sandwich"              amount={city.cost_of_living.sandwich}           cur={city.cost_of_living.currency} />
            <CostRow label="Cheap restaurant meal" amount={city.cost_of_living.cheap_restaurant}  cur={city.cost_of_living.currency} />
            <CostRow label="Laundry / load"       amount={city.cost_of_living.laundry_load}       cur={city.cost_of_living.currency} />
            <CostRow label="Student gym / month"  amount={city.cost_of_living.gym_membership}     cur={city.cost_of_living.currency} />
            <CostRow label="Budget: bare-bones"   amount={city.cost_of_living.student_budget_low} cur={city.cost_of_living.currency} highlight />
            <CostRow label="Total monthly (typical)"  amount={city.cost_of_living.total_monthly}     cur={city.cost_of_living.currency} highlight />
          </div>
        </section>
      )}

      {/* LIFESTYLE COST CALCULATOR */}
      {city.cost_of_living && (
        <LifestyleCalculatorSection col={city.cost_of_living} slug={city.slug} />
      )}

      {/* STUDENT FRIENDLINESS */}
      {city.student_friendliness && (
        <StudentFriendlinessSection sf={city.student_friendliness} />
      )}

      {/* RESEARCH SCORES — Career / Transport / Lifestyle (CSV-derived) */}
      <ResearchScoresSection
        research={city.research_scores}
        cityName={city.name}
        citySlug={city.slug}
        similar={city.similar_lifestyle_cities}
      />

      {/* NEIGHBOURHOODS */}
      {city.neighbourhoods?.length > 0 && (
        <NeighbourhoodsSection cityName={city.name} neighbourhoods={city.neighbourhoods} />
      )}

      {/* HANGOUTS */}
      {city.hangouts?.length > 0 && (
        <HangoutsSection cityName={city.name} hangouts={city.hangouts} />
      )}

      {/* STUDENT VENUES via Google Places */}
      <VenuesSection slug={city.slug} cityName={city.name} />

      {/* STUDENT QUOTES */}
      {city.student_quotes?.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10" data-testid="city-quotes">
          <h2 className="font-editorial text-2xl mb-5">What students say</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {city.student_quotes.map((q, i) => (
              <div key={i} className="bg-white border border-stone-200 rounded-lg p-5">
                <Quote className="h-5 w-5 text-stone-300 mb-2" />
                <p className="text-sm text-stone-800 leading-relaxed italic">"{q.quote}"</p>
                <div className="mt-3 text-xs text-stone-500">
                  <span className="font-mono">{q.subreddit}</span>
                  {q.context && <span className="text-stone-400"> · {q.context}</span>}
                </div>
              </div>
            ))}
          </div>
          {city.subreddit_links?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {city.subreddit_links.map((s) => (
                <a
                  key={s.url} href={s.url} target="_blank" rel="noreferrer noopener"
                  className="text-xs text-stone-600 border border-stone-300 rounded-full px-3 py-1 hover:bg-stone-100 hover:text-[#002147] inline-flex items-center gap-1"
                >
                  {s.label} <ExternalLink className="h-3 w-3" />
                </a>
              ))}
            </div>
          )}
        </section>
      )}

      {/* FIND STUDENTS BEFORE ARRIVAL */}
      {city.find_students_before_arrival?.length > 0 && (
        <FindStudentsSection cityName={city.name} channels={city.find_students_before_arrival} />
      )}

      {/* SAFETY */}
      {city.safety && <SafetySection safety={city.safety} />}

      {/* ARRIVAL CHECKLIST */}
      {city.arrival_checklist && <ArrivalChecklistSection cityName={city.name} arr={city.arrival_checklist} />}

      {/* WEATHER */}
      {city.weather && <WeatherSection cityName={city.name} weather={city.weather} />}

      {/* FAITH & COMMUNITY */}
      {city.faith && <FaithSection faith={city.faith} />}

      {/* FOOD FROM HOME */}
      {city.food_from_home && <FoodFromHomeSection food={city.food_from_home} />}

      {/* GETTING HOME */}
      {city.getting_home && <GettingHomeSection cityName={city.name} gh={city.getting_home} />}

      {/* PART-TIME WORK */}
      {city.part_time_work && <PartTimeWorkSection cityName={city.name} work={city.part_time_work} />}

      {/* POST-STUDY VISA */}
      {city.post_study_visa && <PostStudyVisaSection visa={city.post_study_visa} />}

      {/* ANNUAL EVENTS */}
      {city.annual_events?.length > 0 && <AnnualEventsSection cityName={city.name} events={city.annual_events} />}

      {/* WELLBEING */}
      {city.wellbeing && <WellbeingSection wellbeing={city.wellbeing} />}

      {/* ALL UNIS IN CITY */}
      {city.universities_in_city?.length > 0 && (
        <UniversitiesInCitySection
          cityName={city.name}
          unis={city.universities_in_city}
          featuredIds={new Set(city.featured_universities || [])}
        />
      )}

      {/* Floating "Compare N cities →" nudge — appears once the
          student has shortlisted 2+ cities (from this or any other
          city page). Sits above the fold-out navbar without
          interfering with the main content. Can be dismissed for
          the current session via the × button. */}
      {cityShortlistSlugs.length >= 2 && !nudgeDismissed && (
        <div
          data-testid="city-detail-compare-nudge"
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-[#002147] text-white shadow-xl border border-white/10 pl-4 pr-1.5 py-1.5 animate-in fade-in slide-in-from-bottom-4"
        >
          <span className="inline-flex items-center gap-2 text-sm font-medium">
            <Scale className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">{cityShortlistSlugs.length} cities shortlisted</span>
            <span className="sm:hidden">{cityShortlistSlugs.length} cities</span>
          </span>
          <Link
            to={`/cities/compare?slugs=${cityShortlistSlugs.join(",")}`}
            data-testid="city-detail-compare-cta"
            className="inline-flex items-center gap-1 rounded-full bg-white text-[#002147] px-3 py-1 text-xs font-semibold hover:bg-stone-100 transition"
          >
            Compare {cityShortlistSlugs.length} <span aria-hidden>→</span>
          </Link>
          <button
            type="button"
            onClick={dismissNudge}
            data-testid="city-detail-compare-nudge-dismiss"
            aria-label="Dismiss compare nudge for this session"
            className="h-6 w-6 inline-flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition"
          >
            <span aria-hidden className="text-sm leading-none">×</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- venue section with category switcher ---------------------------
function VenuesSection({ slug, cityName }) {
  const [category, setCategory] = useState("cafe");
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setReason(null);
    (async () => {
      try {
        const res = await api.get(`/cities/${slug}/venues`, { params: { category } });
        if (!cancelled) {
          setVenues(res.data?.venues || []);
          setReason(res.data?.reason || null);
        }
      } catch (e) {
        if (!cancelled) { setVenues([]); setReason(e.message); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, category]);

  const cats = useMemo(() => Object.entries(CATEGORY_META), []);

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10" data-testid="city-venues">
      <div className="flex items-baseline justify-between mb-5">
        <h2 className="font-editorial text-2xl">Where students go in {cityName}</h2>
        <div className="text-xs text-stone-500">Live from Google Places</div>
      </div>
      <div className="flex flex-wrap gap-2 mb-5">
        {cats.map(([key, meta]) => {
          const Icon = meta.icon;
          const active = category === key;
          return (
            <button
              key={key}
              onClick={() => setCategory(key)}
              data-testid={`city-venue-cat-${key}`}
              className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded-full border transition-colors ${
                active
                  ? "bg-[#002147] text-white border-[#002147]"
                  : "bg-white text-stone-700 border-stone-300 hover:border-[#002147]"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {meta.label}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-24 bg-stone-100 rounded-lg animate-pulse" />
          ))}
        </div>
      )}

      {!loading && venues.length === 0 && (
        <div className="text-sm text-stone-500 bg-white border border-stone-200 rounded-lg p-6" data-testid="city-venues-empty">
          {reason
            ? "Live venue lookups aren't configured for this environment yet — we'll wire them up shortly."
            : "No venues found in this category for the current search radius."}
        </div>
      )}

      {!loading && venues.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {venues.map((v) => (
            <a
              key={v.place_id} href={v.google_maps_url} target="_blank" rel="noreferrer noopener"
              className="bg-white border border-stone-200 rounded-lg p-4 hover:border-[#002147] hover:shadow-md transition-all block"
              data-testid={`city-venue-${v.place_id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{v.name}</div>
                  <div className="text-xs text-stone-500 mt-0.5 truncate">{v.address}</div>
                </div>
                {v.rating != null && (
                  <div className="text-xs font-mono bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 shrink-0">
                    ★ {v.rating.toFixed(1)}
                    {v.rating_count ? (
                      <span className="text-amber-700"> · {v.rating_count}</span>
                    ) : null}
                  </div>
                )}
              </div>
              {v.primary_type && (
                <div className="mt-2 flex flex-wrap gap-1">
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                    {v.primary_type.replace(/_/g, " ")}
                  </Badge>
                </div>
              )}
            </a>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------- little primitives ----------------------------------------------
function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className="font-mono text-lg mt-1 text-stone-900">{value}</div>
    </div>
  );
}

function CostRow({ label, amount, cur, highlight }) {
  const { formatFrom, currency } = useCurrency();
  const showConverted = amount && cur && currency !== cur;
  return (
    <div className={highlight ? "bg-emerald-50 border border-emerald-200 rounded p-3" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className={`font-mono mt-0.5 ${highlight ? "text-emerald-900 font-semibold" : "text-stone-900"}`}>
        {amount ? `${currencySymbol(cur)}${amount.toLocaleString()}` : "—"}
      </div>
      {showConverted && (
        <div className="text-[10px] text-stone-500 font-mono mt-0.5">
          ≈ {formatFrom(amount, cur)}
        </div>
      )}
    </div>
  );
}

// ---------- lifestyle cost calculator ---------------------------------------
// Sliders + toggles that let a prospective student personalise the monthly
// cost estimate. Uses the 14 unit-cost fields already on `city_guides.py`.
const HOUSING_CHOICES = [
  { key: "hall",    label: "Student hall / dorm",     amount: (c) => c.student_accommodation },
  { key: "outside", label: "Shared flat (outside)",   amount: (c) => Math.round(c.rent_1br_outside * 0.6) },
  { key: "centre",  label: "Shared flat (centre)",    amount: (c) => Math.round(c.rent_1br_center  * 0.6) },
  { key: "studio",  label: "Studio (1BR, outside)",   amount: (c) => c.rent_1br_outside },
];

const WEEKS_PER_MONTH = 4.33;

// Compact URL-encoding for share links.
//   ?lifestyle=h-centre_e6_ci2_g1_t1_cf3_b3_s2_l1
// Order-independent, only writes non-default values so the URL stays short.
const HOUSING_KEYS = new Set(HOUSING_CHOICES.map((h) => h.key));

function encodeLifestyle(s, defaults) {
  const parts = [];
  if (s.housing !== defaults.housing) parts.push(`h-${s.housing}`);
  if (s.eatOutMo !== defaults.eatOutMo) parts.push(`e${s.eatOutMo}`);
  if (s.cinemaMo !== defaults.cinemaMo) parts.push(`ci${s.cinemaMo}`);
  if (s.gym !== defaults.gym) parts.push(`g${s.gym ? 1 : 0}`);
  if (s.transport !== defaults.transport) parts.push(`t${s.transport ? 1 : 0}`);
  if (s.coffeePerWeek !== defaults.coffeePerWeek) parts.push(`cf${s.coffeePerWeek}`);
  if (s.beerPerWeek !== defaults.beerPerWeek) parts.push(`b${s.beerPerWeek}`);
  if (s.sandwichPerWeek !== defaults.sandwichPerWeek) parts.push(`s${s.sandwichPerWeek}`);
  if (s.laundryPerWeek !== defaults.laundryPerWeek) parts.push(`l${s.laundryPerWeek}`);
  return parts.join("_");
}

function decodeLifestyle(str, defaults) {
  if (!str) return null;
  const out = { ...defaults };
  const parts = str.split("_");
  for (const p of parts) {
    if (p.startsWith("h-")) {
      const v = p.slice(2);
      if (HOUSING_KEYS.has(v)) out.housing = v;
    } else if (p.startsWith("ci")) {
      const n = parseInt(p.slice(2), 10);
      if (!isNaN(n) && n >= 0 && n <= 30) out.cinemaMo = n;
    } else if (p.startsWith("cf")) {
      const n = parseInt(p.slice(2), 10);
      if (!isNaN(n) && n >= 0 && n <= 21) out.coffeePerWeek = n;
    } else if (p.startsWith("e")) {
      const n = parseInt(p.slice(1), 10);
      if (!isNaN(n) && n >= 0 && n <= 30) out.eatOutMo = n;
    } else if (p.startsWith("g")) {
      out.gym = p.slice(1) === "1";
    } else if (p.startsWith("t")) {
      out.transport = p.slice(1) === "1";
    } else if (p.startsWith("b")) {
      const n = parseInt(p.slice(1), 10);
      if (!isNaN(n) && n >= 0 && n <= 21) out.beerPerWeek = n;
    } else if (p.startsWith("s")) {
      const n = parseInt(p.slice(1), 10);
      if (!isNaN(n) && n >= 0 && n <= 14) out.sandwichPerWeek = n;
    } else if (p.startsWith("l")) {
      const n = parseInt(p.slice(1), 10);
      if (!isNaN(n) && n >= 0 && n <= 7) out.laundryPerWeek = n;
    }
  }
  return out;
}

function LifestyleCalculatorSection({ col, slug }) {
  const { formatFrom, currency } = useCurrency();
  const cur = col.currency;
  const storageKey = `wua_lifestyle_${slug}`;
  const [searchParams, setSearchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);

  // Defaults resemble a mid-range "comfortable" student.
  const defaults = {
    housing: "hall",
    eatOutMo: 6, cinemaMo: 2,
    gym: true, transport: true,
    coffeePerWeek: 3, beerPerWeek: 3,
    laundryPerWeek: 1, sandwichPerWeek: 2,
  };

  // Init: URL param wins, then localStorage, then defaults.
  const [state, setState] = useState(() => {
    const fromUrl = decodeLifestyle(searchParams.get("lifestyle"), defaults);
    if (fromUrl) return fromUrl;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return { ...defaults, ...JSON.parse(raw) };
    } catch {}
    return defaults;
  });

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(state)); } catch {}
  }, [state, storageKey]);

  const update = (k, v) => setState((s) => ({ ...s, [k]: v }));
  const reset = () => {
    setState(defaults);
    // Clear the URL param so a reset really is a reset.
    if (searchParams.has("lifestyle")) {
      const next = new URLSearchParams(searchParams);
      next.delete("lifestyle");
      setSearchParams(next, { replace: true });
    }
  };

  const share = async () => {
    const encoded = encodeLifestyle(state, defaults);
    // Update the URL first so what the user copies matches what they see.
    const next = new URLSearchParams(searchParams);
    if (encoded) next.set("lifestyle", encoded); else next.delete("lifestyle");
    setSearchParams(next, { replace: true });
    const url = `${window.location.origin}${window.location.pathname}${encoded ? `?lifestyle=${encoded}` : ""}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Older browsers — fall back to a prompt.
      window.prompt("Copy your share link:", url);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  const housing = HOUSING_CHOICES.find((h) => h.key === state.housing) || HOUSING_CHOICES[0];
  // Halls typically bundle utilities + internet into rent.
  // Shared flats and studios do NOT — students pay these separately.
  const utilitiesBundled = housing.key === "hall";

  const breakdown = useMemo(() => {
    const rent      = housing.amount(col) || 0;
    const groceries = col.groceries_monthly || 0;
    const transport = state.transport ? (col.monthly_transport || 0) : 0;
    const gym       = state.gym ? (col.gym_membership || 0) : 0;
    const mobile    = col.mobile_monthly || 0;              // always yours
    const internet  = utilitiesBundled ? 0 : (col.internet_monthly || 0);
    const utilities = utilitiesBundled ? 0 : (col.utilities_monthly || 0);
    const eatOut    = state.eatOutMo * (col.cheap_restaurant || 0);
    const cinema    = state.cinemaMo * (col.cinema_ticket || 0);
    const coffee    = state.coffeePerWeek  * WEEKS_PER_MONTH * (col.coffee || 0);
    const beer      = state.beerPerWeek    * WEEKS_PER_MONTH * (col.beer_pint || 0);
    const sandwich  = state.sandwichPerWeek * WEEKS_PER_MONTH * (col.sandwich || 0);
    const laundry   = state.laundryPerWeek  * WEEKS_PER_MONTH * (col.laundry_load || 0);
    return { rent, utilities, internet, mobile, groceries, transport, gym,
             eatOut, cinema, coffee, beer, sandwich, laundry };
  }, [state, col, housing, utilitiesBundled]);

  const total = Object.values(breakdown).reduce((a, b) => a + b, 0);
  const nativeTotal = Math.round(total);
  const budgetLow  = col.student_budget_low  || 0;
  const budgetMid  = col.student_budget_mid  || 0;
  const budgetHigh = Math.round((col.student_budget_mid || 0) * 1.35);

  const positionPct = (() => {
    if (nativeTotal <= budgetLow)  return 15;
    if (nativeTotal >= budgetHigh) return 92;
    return 15 + ((nativeTotal - budgetLow) / (budgetHigh - budgetLow)) * 77;
  })();

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10" data-testid="city-lifestyle-calculator">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-editorial text-2xl flex items-center gap-2">
          <Calculator className="h-5 w-5 text-[#4C1D95]" /> Pick your lifestyle
        </h2>
        <div className="flex items-center gap-3">
          <button
            onClick={share}
            data-testid="lifestyle-share"
            className={`text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-colors ${
              copied
                ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                : "bg-white border-stone-300 text-stone-700 hover:border-[#002147] hover:text-[#002147]"
            }`}
          >
            {copied ? (
              <><Check className="h-3 w-3" /> Link copied</>
            ) : (
              <><Share2 className="h-3 w-3" /> Share my budget</>
            )}
          </button>
          <button
            onClick={reset}
            data-testid="lifestyle-reset"
            className="text-xs text-stone-500 hover:text-[#002147] underline decoration-dotted"
          >
            Reset
          </button>
        </div>
      </div>
      <p className="text-sm text-stone-600 max-w-2xl mb-5">
        Drag the sliders to match your real habits — every count is total
        units per week or per month (not "sessions"). We'll build a monthly
        estimate in {currency} using the unit costs we tracked for{" "}
        {col.currency === "GBP" ? "the UK" : "the US"}
        {" "}(source: Numbeo, {col.as_of}).
      </p>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* --- controls --- */}
        <div className="lg:col-span-2 bg-white border border-stone-200 rounded-lg p-6 space-y-6">
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-2">Where you live</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {HOUSING_CHOICES.map((h) => (
                <button
                  key={h.key}
                  onClick={() => update("housing", h.key)}
                  data-testid={`lifestyle-housing-${h.key}`}
                  className={`text-xs px-3 py-2 rounded-lg border transition-colors text-left ${
                    state.housing === h.key
                      ? "bg-[#002147] text-white border-[#002147]"
                      : "bg-white text-stone-700 border-stone-300 hover:border-[#002147]"
                  }`}
                >
                  <div className="font-semibold">{h.label}</div>
                  <div className={`font-mono text-[10px] mt-0.5 ${state.housing === h.key ? "text-white/70" : "text-stone-500"}`}>
                    {currencySymbol(cur)}{(h.amount(col) || 0).toLocaleString()}/mo
                  </div>
                </button>
              ))}
            </div>
          </div>

          <SliderRow
            label="Meals out at a cheap restaurant"
            unitDesc={`1 meal @ ${currencySymbol(cur)}${col.cheap_restaurant}`}
            unit="× / month"
            value={state.eatOutMo} onChange={(v) => update("eatOutMo", v)}
            min={0} max={20} step={1}
            costPerUnit={col.cheap_restaurant} cur={cur} testId="lifestyle-slider-eatout"
          />
          <SliderRow
            label="Cinema tickets"
            unitDesc={`1 ticket @ ${currencySymbol(cur)}${col.cinema_ticket}`}
            unit="× / month"
            value={state.cinemaMo} onChange={(v) => update("cinemaMo", v)}
            min={0} max={10} step={1}
            costPerUnit={col.cinema_ticket} cur={cur} testId="lifestyle-slider-cinema"
          />
          <SliderRow
            label="Coffees at a café"
            unitDesc={`1 coffee @ ${currencySymbol(cur)}${col.coffee}`}
            unit="× / week"
            value={state.coffeePerWeek} onChange={(v) => update("coffeePerWeek", v)}
            min={0} max={14} step={1}
            costPerUnit={col.coffee} cur={cur} testId="lifestyle-slider-coffee"
          />
          <SliderRow
            label="Pints of beer (total per week)"
            unitDesc={`1 pint @ ${currencySymbol(cur)}${col.beer_pint} — spread across any number of nights`}
            unit="pints / week"
            value={state.beerPerWeek} onChange={(v) => update("beerPerWeek", v)}
            min={0} max={20} step={1}
            costPerUnit={col.beer_pint} cur={cur} testId="lifestyle-slider-beer"
          />
          <SliderRow
            label="Sandwich lunches"
            unitDesc={`1 sandwich @ ${currencySymbol(cur)}${col.sandwich}`}
            unit="× / week"
            value={state.sandwichPerWeek} onChange={(v) => update("sandwichPerWeek", v)}
            min={0} max={7} step={1}
            costPerUnit={col.sandwich} cur={cur} testId="lifestyle-slider-sandwich"
          />
          <SliderRow
            label="Laundry loads"
            unitDesc={`1 load @ ${currencySymbol(cur)}${col.laundry_load}`}
            unit="× / week"
            value={state.laundryPerWeek} onChange={(v) => update("laundryPerWeek", v)}
            min={0} max={4} step={1}
            costPerUnit={col.laundry_load} cur={cur} testId="lifestyle-slider-laundry"
          />

          <div className="flex items-center justify-between pt-2 border-t border-stone-100">
            <div>
              <div className="text-sm font-medium">Student gym membership</div>
              <div className="text-xs text-stone-500 font-mono">
                {currencySymbol(cur)}{(col.gym_membership || 0).toLocaleString()}/mo
              </div>
            </div>
            <Switch
              checked={state.gym}
              onCheckedChange={(v) => update("gym", v)}
              data-testid="lifestyle-toggle-gym"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Monthly transport pass</div>
              <div className="text-xs text-stone-500 font-mono">
                {currencySymbol(cur)}{(col.monthly_transport || 0).toLocaleString()}/mo
              </div>
            </div>
            <Switch
              checked={state.transport}
              onCheckedChange={(v) => update("transport", v)}
              data-testid="lifestyle-toggle-transport"
            />
          </div>
        </div>

        {/* --- total panel --- */}
        <div className="bg-gradient-to-br from-[#002147] to-[#4C1D95] text-white rounded-lg p-6 flex flex-col" data-testid="lifestyle-total-panel">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/70 mb-3">
            <Wallet className="h-3 w-3" /> Your estimated monthly cost
          </div>
          <div className="font-chip text-4xl font-bold" data-testid="lifestyle-total-native">
            {currencySymbol(cur)}{nativeTotal.toLocaleString()}
          </div>
          {cur !== currency && (
            <div className="font-mono text-lg text-white/80 mt-1" data-testid="lifestyle-total-display">
              ≈ {formatFrom(nativeTotal, cur)}
            </div>
          )}

          {/* Position marker */}
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-wider text-white/60 mb-2">Where you sit</div>
            <div className="relative h-2 rounded-full bg-white/20 overflow-visible">
              <div
                className="absolute -top-1 h-4 w-1 bg-white rounded"
                style={{ left: `${positionPct}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-white/60 mt-1.5 font-mono">
              <span>{currencySymbol(cur)}{budgetLow.toLocaleString()}</span>
              <span>{currencySymbol(cur)}{budgetMid.toLocaleString()}</span>
              <span>{currencySymbol(cur)}{budgetHigh.toLocaleString()}+</span>
            </div>
            <div className="flex justify-between text-[10px] uppercase tracking-wider text-white/60 mt-0.5">
              <span>Bare-bones</span>
              <span>Typical</span>
              <span>Comfortable</span>
            </div>
          </div>

          {/* Breakdown */}
          <div className="mt-6 pt-6 border-t border-white/20 space-y-1.5 text-xs font-mono">
            <BreakdownLine label="Housing"        v={breakdown.rent}      cur={cur} />
            {breakdown.utilities > 0 && <BreakdownLine label="Utilities"  v={breakdown.utilities} cur={cur} />}
            {breakdown.internet > 0  && <BreakdownLine label="Internet"   v={breakdown.internet}  cur={cur} />}
            <BreakdownLine label="Mobile phone"   v={breakdown.mobile}    cur={cur} />
            <BreakdownLine label="Groceries"      v={breakdown.groceries} cur={cur} />
            {breakdown.transport > 0 && <BreakdownLine label="Transport" v={breakdown.transport} cur={cur} />}
            {breakdown.gym > 0       && <BreakdownLine label="Gym"        v={breakdown.gym}       cur={cur} />}
            <BreakdownLine label="Eating out"    v={breakdown.eatOut}    cur={cur} />
            <BreakdownLine label="Cinema"        v={breakdown.cinema}    cur={cur} />
            <BreakdownLine label="Coffee"        v={breakdown.coffee}    cur={cur} />
            <BreakdownLine label="Beer"          v={breakdown.beer}      cur={cur} />
            <BreakdownLine label="Sandwich"      v={breakdown.sandwich}  cur={cur} />
            <BreakdownLine label="Laundry"       v={breakdown.laundry}   cur={cur} />
          </div>
          {utilitiesBundled && (
            <div className="mt-3 pt-3 border-t border-white/10 text-[10px] text-white/60 leading-relaxed" data-testid="lifestyle-utilities-note">
              Halls typically bundle utilities + internet into rent — we've excluded them from your total. Switch to a shared flat above to see the full picture.
            </div>
          )}
        </div>
      </div>

      {/* Assumptions — collapsible so power-users can verify the math */}
      <details className="mt-5 bg-white border border-stone-200 rounded-lg" data-testid="lifestyle-assumptions">
        <summary className="cursor-pointer px-5 py-3 text-sm font-medium text-stone-700 hover:text-[#002147] flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Calculator className="h-4 w-4" /> How we calculated this
          </span>
          <span className="text-xs text-stone-400">Click to expand</span>
        </summary>
        <div className="px-5 pb-5 pt-1 text-sm text-stone-700 space-y-3 leading-relaxed">
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Unit costs (source: <a href={col.source_url} className="underline decoration-dotted" target="_blank" rel="noreferrer noopener">Numbeo</a>, {col.as_of})</div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1 font-mono text-xs mt-2">
              <li>· 1 pint of beer = {currencySymbol(cur)}{col.beer_pint}</li>
              <li>· 1 coffee = {currencySymbol(cur)}{col.coffee}</li>
              <li>· 1 cinema ticket = {currencySymbol(cur)}{col.cinema_ticket}</li>
              <li>· 1 sandwich = {currencySymbol(cur)}{col.sandwich}</li>
              <li>· 1 cheap-restaurant meal = {currencySymbol(cur)}{col.cheap_restaurant}</li>
              <li>· 1 laundry load = {currencySymbol(cur)}{col.laundry_load}</li>
              <li>· Student gym membership = {currencySymbol(cur)}{col.gym_membership}/mo</li>
              <li>· Monthly transport pass = {currencySymbol(cur)}{col.monthly_transport}/mo</li>
              <li>· Groceries = {currencySymbol(cur)}{col.groceries_monthly}/mo (weekly shop ≈ {currencySymbol(cur)}{col.weekly_shop})</li>
              <li>· Utilities (energy + water) = {currencySymbol(cur)}{col.utilities_monthly}/mo</li>
              <li>· Home broadband = {currencySymbol(cur)}{col.internet_monthly}/mo</li>
              <li>· Mobile phone plan = {currencySymbol(cur)}{col.mobile_monthly}/mo</li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Housing formulas</div>
            <ul className="text-xs space-y-0.5">
              <li>· <strong>Student hall</strong> = the city's typical hall/dorm rate ({currencySymbol(cur)}{col.student_accommodation}/mo). <strong>Utilities + internet bundled</strong> — we don't add them separately.</li>
              <li>· <strong>Shared flat (outside)</strong> = 60% of the 1BR-outside rent ({currencySymbol(cur)}{Math.round(col.rent_1br_outside * 0.6)}/mo), assuming a 2-bed with one flatmate. Utilities + internet <strong>added separately</strong>.</li>
              <li>· <strong>Shared flat (centre)</strong> = 60% of the 1BR-centre rent ({currencySymbol(cur)}{Math.round(col.rent_1br_center * 0.6)}/mo). Utilities + internet added separately.</li>
              <li>· <strong>Studio</strong> = the full 1BR-outside rent ({currencySymbol(cur)}{col.rent_1br_outside}/mo), no flatmates. Utilities + internet added separately.</li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Frequency → monthly</div>
            <p className="text-xs">
              Sliders labelled "× / week" multiply by <span className="font-mono">4.33</span> (the average weeks per month) to get a monthly cost. Sliders labelled "× / month" are used directly.
            </p>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">What's NOT included</div>
            <ul className="text-xs space-y-0.5">
              <li>· Tuition fees (see the university profile pages).</li>
              <li>· One-off costs: flights home, security deposit, textbooks, laptop.</li>
              <li>· Health insurance / NHS surcharge (international students).</li>
              <li>· Contents insurance and TV licence (~{currencySymbol(cur)}15/mo combined in the UK).</li>
            </ul>
          </div>

          <div className="text-[11px] text-stone-500 italic pt-2 border-t border-stone-100">
            All figures are averages from a public dataset — your actual costs will vary based on neighbourhood, flatmates, and how much you cook at home. Groceries in particular can drop 40–50% if you cook and take packed lunches.
          </div>
        </div>
      </details>
    </section>
  );
}

function SliderRow({ label, unitDesc, unit, value, onChange, min, max, step, costPerUnit, cur, testId }) {
  const monthly = (
    unit.toLowerCase().includes("month")
      ? value * (costPerUnit || 0)
      : value * WEEKS_PER_MONTH * (costPerUnit || 0)
  );
  return (
    <div>
      <div className="flex items-baseline justify-between mb-0.5 gap-3">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-stone-500 shrink-0">
          <span className="font-mono font-semibold text-stone-900">{value}</span> {unit} ·{" "}
          <span className="font-mono">{currencySymbol(cur)}{Math.round(monthly).toLocaleString()}</span>/mo
        </div>
      </div>
      {unitDesc && (
        <div className="text-[11px] text-stone-500 mb-1.5">{unitDesc}</div>
      )}
      <Slider
        value={[value]}
        min={min} max={max} step={step}
        onValueChange={(v) => onChange(v[0])}
        data-testid={testId}
      />
    </div>
  );
}

function BreakdownLine({ label, v, cur }) {
  if (!v) return null;
  return (
    <div className="flex items-baseline justify-between text-white/90">
      <span className="text-white/60 uppercase tracking-wider text-[10px]">{label}</span>
      <span>{currencySymbol(cur)}{Math.round(v).toLocaleString()}</span>
    </div>
  );
}


// ---------- new editorial sections ------------------------------------------
function StudentFriendlinessSection({ sf }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10" data-testid="city-friendliness">
      <div className="flex items-baseline justify-between mb-5">
        <h2 className="font-editorial text-2xl">Student-friendliness</h2>
        <div className="flex items-center gap-1 text-amber-600" data-testid="city-friendliness-score">
          {[...Array(5)].map((_, i) => (
            <Sparkles key={i} className={`h-4 w-4 ${i < (sf.score || 0) ? "fill-amber-400 text-amber-400" : "text-stone-300"}`} />
          ))}
          <span className="text-xs text-stone-600 ml-2 font-mono">{sf.score}/5</span>
        </div>
      </div>
      <p className="text-stone-700 leading-relaxed mb-6 max-w-3xl">{sf.summary}</p>
      <div className="grid md:grid-cols-2 gap-6">
        {sf.discounts?.length > 0 && (
          <div className="bg-white border border-stone-200 rounded-lg p-5" data-testid="city-discounts">
            <h3 className="font-editorial text-lg mb-3 flex items-center gap-2">
              <Heart className="h-4 w-4 text-rose-500" /> Discounts & schemes
            </h3>
            <ul className="space-y-2">
              {sf.discounts.map((d, i) => (
                <li key={i} className="text-sm text-stone-700 flex items-start gap-2">
                  <span className="text-rose-400 font-bold mt-0.5">·</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {sf.facilities?.length > 0 && (
          <div className="bg-white border border-stone-200 rounded-lg p-5" data-testid="city-facilities">
            <h3 className="font-editorial text-lg mb-3 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-emerald-500" /> Facilities & perks
            </h3>
            <ul className="space-y-2">
              {sf.facilities.map((f, i) => (
                <li key={i} className="text-sm text-stone-700 flex items-start gap-2">
                  <span className="text-emerald-500 font-bold mt-0.5">·</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

// ---- Research Scores (CSV-derived career / transport / lifestyle) ----
// Lifestyle radar — overlays the current city + up to 3 similar cities.

// Lifestyle radar — overlays the current city + up to 3 similar cities.
const RADAR_PALETTE = [
  { stroke: "#7C3AED", fill: "#7C3AED", opacity: 0.35 },  // primary (this city)
  { stroke: "#F59E0B", fill: "#F59E0B", opacity: 0.14 },  // amber
  { stroke: "#EC4899", fill: "#EC4899", opacity: 0.12 },  // pink
  { stroke: "#10B981", fill: "#10B981", opacity: 0.12 },  // emerald
];

// Canonical axis orderings used by the three radars. Missing scores
// render as 0 on that axis.
const CAREER_ORDER = [
  "research", "tech", "engineering", "healthcare", "finance", "law",
  "creative", "government", "startups", "fintech", "biotech",
  "aerospace", "cleantech", "gaming", "publishing", "content_creation",
  "agencies", "ecommerce", "mobility", "agritech", "edtech",
];
const CAREER_SHORT = {
  research: "Research", tech: "Software", engineering: "Engineering",
  healthcare: "Health", finance: "Finance", law: "Law",
  creative: "Creative", government: "Gov", startups: "Startups",
  fintech: "Fintech", biotech: "Biotech", aerospace: "Aerospace",
  cleantech: "Cleantech", gaming: "Gaming", ecommerce: "Ecom",
  mobility: "Mobility", agritech: "Agri", edtech: "EdTech",
  publishing: "Publishing", content_creation: "Content", agencies: "Agencies",
};
const TRANSPORT_ORDER = ["rail", "air", "walk", "cycle", "night"];
const TRANSPORT_SHORT = {
  rail: "Rail / PT", air: "Air", walk: "Walk", cycle: "Cycle", night: "Night",
};

/** Compact strip showing this city's global rank in every vertical we track.
 *  Backed by `research.vertical_ranks` from GET /cities/{slug} (routes/cities.py).
 *  Click a tile → `/cities/hubs/{tag}` for the full ranked list. */
function VerticalRankStrip({ ranks }) {
  const entries = Object.entries(ranks || {})
    .filter(([, info]) => info && info.rank)
    .sort(([, a], [, b]) => a.rank - b.rank);

  if (entries.length === 0) return null;

  const bandClass = (rank, total) => {
    if (rank <= 10) return "bg-emerald-50 border-emerald-200 text-emerald-900";
    if (rank <= 25) return "bg-sky-50 border-sky-200 text-sky-900";
    if (rank <= 50) return "bg-amber-50 border-amber-200 text-amber-900";
    const pct = total ? rank / total : 1;
    if (pct <= 0.5) return "bg-stone-50 border-stone-200 text-stone-700";
    return "bg-stone-50 border-stone-200 text-stone-500";
  };

  return (
    <div className="mb-8" data-testid="city-vertical-rank-strip">
      <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 uppercase mb-2">
        Global rank per sector
      </div>
      <div className="flex flex-wrap gap-2">
        {entries.map(([tag, info]) => (
          <Link
            key={tag}
            to={`/cities/hubs/${tag}`}
            data-testid={`vertical-rank-${tag}`}
            className={`inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-md border text-[12px] hover:brightness-105 transition ${bandClass(info.rank, info.total)}`}
            title={`${CAREER_SHORT[tag] || tag}: ranked #${info.rank} of ${info.total} cities (score ${info.score}/10)`}
          >
            <span className="font-semibold">{CAREER_SHORT[tag] || tag}</span>
            <span className="font-mono text-[11px]">#{info.rank}</span>
            <span className="text-[10px] opacity-70">of {info.total}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function ResearchRadar({ order, labels, cityName, primaryScores, comparisons, activeSlugs, palettePrimary, height = 340 }) {
  // Trim axes to those that have any signal across primary + shown comparisons.
  const shownSlugs = new Set([...(comparisons || []).filter((c) => activeSlugs.has(c.slug)).map((c) => c.slug)]);
  const relevant = order.filter((k) => {
    if ((primaryScores || {})[k]) return true;
    return (comparisons || []).some((c) => shownSlugs.has(c.slug) && (c.scores || {})[k]);
  });
  const axes = relevant.length >= 4 ? relevant : order.slice(0, Math.min(order.length, 5));

  const data = axes.map((k) => {
    const row = { axis: labels[k] || k, key: k };
    row[cityName] = (primaryScores || {})[k] ?? 0;
    (comparisons || []).forEach((c) => {
      row[c.name] = (c.scores || {})[k] ?? 0;
    });
    return row;
  });

  return (
    <div className="w-full" style={{ height }}>
      <ResponsiveContainer>
        <RadarChart data={data} outerRadius="72%" margin={{ top: 12, right: 32, bottom: 8, left: 32 }}>
          <PolarGrid stroke="#e7e5e4" />
          <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10, fill: "#57534e" }} tickLine={false} />
          <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fontSize: 9, fill: "#a8a29e" }} tickCount={6} />
          <Tooltip formatter={(v) => `${v}/10`} />
          <Radar name={cityName} dataKey={cityName}
                 stroke={palettePrimary.stroke} fill={palettePrimary.fill}
                 fillOpacity={palettePrimary.opacity} strokeWidth={2.5} />
          {(comparisons || []).map((c, i) => (
            activeSlugs.has(c.slug) ? (
              <Radar key={c.slug} name={c.name} dataKey={c.name}
                     stroke={RADAR_PALETTE[(i % 3) + 1].stroke}
                     fill={RADAR_PALETTE[(i % 3) + 1].fill}
                     fillOpacity={RADAR_PALETTE[(i % 3) + 1].opacity}
                     strokeWidth={1.5} />
            ) : null
          ))}
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

function CityCombobox({ allCities, exclude, onPick, onClose }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!allCities) return [];
    const ex = new Set(exclude || []);
    const needle = q.trim().toLowerCase();
    let pool = allCities.filter((c) => !ex.has(c.slug));
    if (needle) {
      pool = pool.filter((c) =>
        (c.name || "").toLowerCase().includes(needle) ||
        (c.country || "").toLowerCase().includes(needle)
      );
    }
    return pool.slice(0, 40);
  }, [q, allCities, exclude]);
  return (
    <div className="absolute z-30 top-full left-0 mt-1 w-64 bg-white border border-stone-200 rounded-lg shadow-lg p-2"
         data-testid="research-city-combobox">
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search city or country…"
        className="w-full text-sm px-2 py-1.5 border border-stone-200 rounded outline-none focus:border-[#7C3AED]"
      />
      <div className="mt-2 max-h-64 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="text-xs text-stone-400 px-2 py-2">No matches</div>
        ) : (
          filtered.map((c) => (
            <button
              key={c.slug}
              onClick={() => { onPick(c); onClose(); }}
              className="w-full text-left px-2 py-1.5 text-[13px] rounded hover:bg-violet-50 flex items-center justify-between"
              data-testid={`research-combobox-pick-${c.slug}`}
            >
              <span className="text-stone-800">{c.name}</span>
              <span className="text-[10px] text-stone-400 font-mono">{c.country_code || ""}</span>
            </button>
          ))
        )}
      </div>
      <button onClick={onClose}
              className="mt-1 text-[11px] text-stone-500 hover:text-[#7C3AED] px-2 py-1 w-full text-left">
        Close
      </button>
    </div>
  );
}

function ComparisonPicker({ primary, comparisons, activeSlugs, setActiveSlugs, onSwap, onRemove, onResetAuto, allCities }) {
  const [openIdx, setOpenIdx] = useState(null);
  const toggle = (slug) => setActiveSlugs((prev) => {
    const next = new Set(prev);
    next.has(slug) ? next.delete(slug) : next.add(slug);
    return next;
  });
  const excludeSlugs = [primary.slug, ...comparisons.map((c) => c.slug)];

  return (
    <div className="bg-white border border-stone-200 rounded-lg px-4 py-3 mb-5" data-testid="research-comparison-picker">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mr-1">Comparing</span>
          <span className="inline-flex items-center gap-1.5 text-[12px] text-stone-800 px-2.5 py-1 rounded-full bg-violet-50 border border-violet-200">
            <span className="w-2 h-2 rounded-full" style={{ background: RADAR_PALETTE[0].stroke }} />
            {primary.name}
            <span className="text-[10px] text-stone-400 font-mono">this city</span>
          </span>
          {comparisons.map((c, i) => {
            const on = activeSlugs.has(c.slug);
            const pal = RADAR_PALETTE[(i % 3) + 1];
            return (
              <div key={c.slug} className="relative inline-flex items-center gap-0.5">
                <button
                  onClick={() => toggle(c.slug)}
                  data-testid={`research-comparison-toggle-${c.slug}`}
                  className={`inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-l-full border-y border-l transition ${
                    on
                      ? "bg-white border-stone-300 text-stone-800"
                      : "bg-stone-50 border-stone-200 text-stone-400 line-through"
                  }`}
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: on ? pal.stroke : "#d6d3d1" }} />
                  {c.name}
                  {c.country_code && <span className="text-[10px] text-stone-400 font-mono">{c.country_code}</span>}
                </button>
                <button
                  onClick={() => setOpenIdx(openIdx === i ? null : i)}
                  data-testid={`research-comparison-swap-${i}`}
                  className="text-[11px] px-1.5 py-1 border-y border-stone-300 text-stone-500 hover:text-[#7C3AED] hover:border-[#7C3AED]"
                  title="Swap city"
                >
                  ▾
                </button>
                <button
                  onClick={() => onRemove(i)}
                  data-testid={`research-comparison-remove-${i}`}
                  className="text-[11px] px-1.5 py-1 rounded-r-full border border-l-0 border-stone-300 text-stone-400 hover:text-red-500 hover:border-red-300"
                  title="Remove city"
                >
                  ×
                </button>
                {openIdx === i && (
                  <CityCombobox
                    allCities={allCities}
                    exclude={excludeSlugs}
                    onPick={(picked) => onSwap(i, picked)}
                    onClose={() => setOpenIdx(null)}
                  />
                )}
              </div>
            );
          })}
          {comparisons.length < 3 && (
            <div className="relative inline-block">
              <button
                onClick={() => setOpenIdx(openIdx === "add" ? null : "add")}
                data-testid="research-comparison-add"
                className="text-[12px] px-2.5 py-1 border border-dashed border-stone-300 rounded-full text-stone-500 hover:text-[#7C3AED] hover:border-[#7C3AED]"
              >
                + Add city
              </button>
              {openIdx === "add" && (
                <CityCombobox
                  allCities={allCities}
                  exclude={excludeSlugs}
                  onPick={(picked) => onSwap(comparisons.length, picked)}
                  onClose={() => setOpenIdx(null)}
                />
              )}
            </div>
          )}
        </div>
        <button
          onClick={onResetAuto}
          className="text-[11px] text-stone-500 hover:text-[#7C3AED] hover:underline shrink-0"
          data-testid="research-comparison-reset"
        >
          ↺ Reset to auto-suggested
        </button>
      </div>
    </div>
  );
}

function ResearchScoresSection({ research, cityName, citySlug, similar }) {
  const empty = !research || (
    Object.keys(research.career || {}).length === 0 &&
    Object.keys(research.transport || {}).length === 0 &&
    Object.keys(research.lifestyle || {}).length === 0
  );

  // Auto-suggested comparison cities: from backend's similar_lifestyle_cities.
  const auto = useMemo(() => (similar || []).slice(0, 3).map((s) => ({
    slug: s.slug, name: s.name, country_code: s.country_code,
    scores: {
      career:    (s.research_scores || {}).career    || {},
      transport: (s.research_scores || {}).transport || {},
      lifestyle: (s.research_scores || {}).lifestyle || {},
    },
  })), [similar]);

  const [comparisons, setComparisons] = useState(auto);
  const [activeSlugs, setActiveSlugs] = useState(() => new Set(auto.map((c) => c.slug)));
  const [allCities, setAllCities] = useState(null);

  // Reset when parent city changes.
  useEffect(() => { setComparisons(auto); setActiveSlugs(new Set(auto.map((c) => c.slug))); }, [auto]);

  // Lazy-load city index for the picker on first open.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/cities");
        if (!cancelled) setAllCities(data.cities || []);
      } catch { /* silently ignore — picker just shows "No matches" */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSwap = async (idx, picked) => {
    try {
      const { data } = await api.get(`/cities/${picked.slug}/research-scores`);
      const rs = data.research_scores || { career: {}, transport: {}, lifestyle: {} };
      const entry = {
        slug: data.slug || picked.slug,
        name: data.name || picked.name,
        country_code: data.country_code || picked.country_code,
        scores: {
          career:    rs.career    || {},
          transport: rs.transport || {},
          lifestyle: rs.lifestyle || {},
        },
      };
      setComparisons((prev) => {
        const next = [...prev];
        next[idx] = entry;
        return next.slice(0, 3);
      });
      setActiveSlugs((prev) => new Set([...prev, entry.slug]));
    } catch { /* keep old */ }
  };
  const handleRemove = (idx) => {
    setComparisons((prev) => prev.filter((_, i) => i !== idx));
  };
  const handleResetAuto = () => {
    setComparisons(auto);
    setActiveSlugs(new Set(auto.map((c) => c.slug)));
  };

  const primary = { slug: citySlug, name: cityName };
  const primaryPal = RADAR_PALETTE[0];
  const topCareer = research && Object.keys(research.career || {}).sort(
    (a, b) => (research.career[b] - research.career[a])
  )[0];

  // Hide the whole section when we have no cluster data for this city —
  // product rule: an empty cluster field should be invisible, not shown
  // as a "Research data pending" placeholder.
  if (empty) return null;

  // The "Cluster scores for X" header introduces the per-sector global
  // rank strip immediately below it. When this city isn't ranked in
  // *any* sector (empty `vertical_ranks`), the header is orphaned and
  // visually confusing — hide it. The peer-city comparison below is
  // its own section and should remain untouched.
  const hasVerticalRanks = Object.values(research.vertical_ranks || {})
    .some((info) => info && info.rank);

  return (
    <section id="city-research-scores" className="max-w-7xl mx-auto px-6 lg:px-10 pb-10" data-testid="city-research-scores">
      {hasVerticalRanks && (
        <>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-editorial text-2xl">Global Industry Cluster Rankings for {cityName}</h2>
            <span className="text-xs text-stone-400">All axes on the same 0-10 scale</span>
          </div>
          <VerticalRankStrip ranks={research.vertical_ranks} />
        </>
      )}
      <div className={`flex items-baseline justify-between mb-3 ${hasVerticalRanks ? "mt-10" : ""}`}>
        <h2 className="font-editorial text-2xl">Peer city comparison</h2>
        <span className="text-xs text-stone-400">Toggle, swap, or add up to 3 peers</span>
      </div>
      <ComparisonPicker
        primary={primary}
        comparisons={comparisons}
        activeSlugs={activeSlugs}
        setActiveSlugs={setActiveSlugs}
        onSwap={handleSwap}
        onRemove={handleRemove}
        onResetAuto={handleResetAuto}
        allCities={allCities}
      />
      <div className="grid gap-5 lg:grid-cols-3">
            <RadarBlock
              title="Career & industries"
              hubTag={topCareer}
              blurb="Sector strengths on a 0-10 scale, drawn from our LinkedIn / Crunchbase / hub-ranking research CSV."
              testId="city-research-career"
              accent="text-[#7C3AED]"
              order={CAREER_ORDER}
              labels={CAREER_SHORT}
              cityName={cityName}
              primaryScores={research.career}
              comparisons={comparisons.map((c) => ({ ...c, scores: c.scores.career }))}
              activeSlugs={activeSlugs}
              palettePrimary={primaryPal}
              extraChip={
                (research.career || {}).creative && research.creative_provenance ? (
                  <span className="inline-flex items-center gap-1.5 text-[10px] text-stone-500">
                    <Palette className="h-3 w-3 text-[#7C3AED]" />
                    Creative & Media:
                    <CreativeProvenanceChip
                      provenance={research.creative_provenance}
                      testId="city-detail-creative-provenance-chip"
                    />
                  </span>
                ) : null
              }
            />
            <RadarBlock
              title="Transport & access"
              hubTag={null}
              blurb="Public transport, air connectivity, walkability, cycling, night transport — CSV /10 scores."
              testId="city-research-transport"
              accent="text-[#0891B2]"
              order={TRANSPORT_ORDER}
              labels={TRANSPORT_SHORT}
              cityName={cityName}
              primaryScores={research.transport}
              comparisons={comparisons.map((c) => ({ ...c, scores: c.scores.transport }))}
              activeSlugs={activeSlugs}
              palettePrimary={{ stroke: "#0891B2", fill: "#0891B2", opacity: 0.3 }}
            />
            <RadarBlock
              title="Lifestyle & culture"
              hubTag="creative"
              blurb="Thirteen lifestyle dimensions scored 0-10 in our research CSV. A score of 7+ qualifies a city for the matching Matcher chip."
              testId="city-research-lifestyle"
              accent="text-[#7C3AED]"
              order={LIFESTYLE_ORDER}
              labels={LIFESTYLE_SHORT}
              cityName={cityName}
              primaryScores={research.lifestyle}
              comparisons={comparisons.map((c) => ({ ...c, scores: c.scores.lifestyle }))}
              activeSlugs={activeSlugs}
              palettePrimary={primaryPal}
            />
          </div>
    </section>
  );
}

function RadarBlock({ title, hubTag, blurb, testId, accent, order, labels, cityName, primaryScores, comparisons, activeSlugs, palettePrimary, extraChip }) {
  const nonEmpty = primaryScores && Object.keys(primaryScores).length > 0;
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-editorial text-lg text-[#002147]">{title}</h3>
        {hubTag && (
          <Link
            to={`/cities/hubs/${hubTag}`}
            className={`text-[11px] hover:underline inline-flex items-center gap-0.5 ${accent}`}
            data-testid={`${testId}-methodology-link`}
          >
            How we score this <ExternalLink className="h-3 w-3" />
          </Link>
        )}
      </div>
      {extraChip && (
        <div className="mb-2" data-testid={`${testId}-extra-chip`}>{extraChip}</div>
      )}
      {nonEmpty ? (
        <ResearchRadar
          order={order}
          labels={labels}
          cityName={cityName}
          primaryScores={primaryScores}
          comparisons={comparisons}
          activeSlugs={activeSlugs}
          palettePrimary={palettePrimary}
          height={340}
        />
      ) : (
        <div className="text-xs text-stone-500 italic border border-dashed border-stone-200 rounded px-3 py-10 text-center">
          Research data pending for this city — check back soon.
        </div>
      )}
      <p className="text-[11px] text-stone-400 mt-3">{blurb}</p>
    </div>
  );
}

function NeighbourhoodsSection({ cityName, neighbourhoods }) {
  const { formatFrom } = useCurrency();
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10" data-testid="city-neighbourhoods">
      <h2 className="font-editorial text-2xl mb-5">Where students cluster in {cityName}</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {neighbourhoods.map((n, i) => (
          <div key={i} className="bg-white border border-stone-200 rounded-lg p-5" data-testid={`city-neighbourhood-${i}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="font-editorial text-lg flex items-center gap-1.5">
                <Home className="h-4 w-4 text-[#002147]" /> {n.name}
              </h3>
              <span className="text-xs font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 shrink-0">
                {n.budget}
              </span>
            </div>
            <p className="text-sm text-stone-700 leading-relaxed">{n.vibe}</p>
            {n.typical_rent_pcm && (
              <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-stone-500">Typical rent</span>
                <span className="font-mono text-sm">
                  {currencySymbol(n.currency)}{n.typical_rent_pcm.toLocaleString()}<span className="text-stone-400 text-xs">/mo</span>
                  <div className="text-[10px] text-stone-500 text-right">≈ {formatFrom(n.typical_rent_pcm, n.currency)}</div>
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function HangoutsSection({ cityName, hangouts }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10" data-testid="city-hangouts">
      <h2 className="font-editorial text-2xl mb-5">Where students actually hang out</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {hangouts.map((h, i) => (
          <div key={i} className="bg-white border border-stone-200 rounded-lg p-5" data-testid={`city-hangout-${i}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="font-editorial text-lg flex items-center gap-1.5">
                <Compass className="h-4 w-4 text-[#4C1D95]" /> {h.name}
              </h3>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider shrink-0">
                {(h.type || "spot").replace(/_/g, " ")}
              </Badge>
            </div>
            {h.area && (
              <div className="text-xs text-stone-500 mb-2 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {h.area}
              </div>
            )}
            <p className="text-sm text-stone-700 leading-relaxed">{h.why}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function FindStudentsSection({ cityName, channels }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10" data-testid="city-find-students">
      <h2 className="font-editorial text-2xl mb-3">Find other students before you arrive</h2>
      <p className="text-sm text-stone-600 max-w-3xl mb-5">
        Following these channels 2-3 months before your intake is the single
        biggest hack for arriving with friends in {cityName}.
      </p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {channels.map((c, i) => (
          <a
            key={i} href={c.url} target="_blank" rel="noreferrer noopener"
            className="bg-white border border-stone-200 rounded-lg p-5 hover:border-[#002147] hover:shadow-md transition-all block"
            data-testid={`city-channel-${i}`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="font-editorial text-base flex items-center gap-1.5">
                <MessageCircle className="h-4 w-4 text-[#002147]" /> {c.channel}
              </h3>
              <ExternalLink className="h-3.5 w-3.5 text-stone-400 shrink-0" />
            </div>
            {c.note && <p className="text-xs text-stone-600 leading-relaxed">{c.note}</p>}
          </a>
        ))}
      </div>
    </section>
  );
}

function UniversitiesInCitySection({ cityName, unis, featuredIds }) {
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const { has: hasPinned, toggle: togglePin, full, ids: pinnedIds, compareHref } = useCompareShortlist();
  const pinnedInThisCity = useMemo(
    () => pinnedIds.filter((id) => unis.some((u) => u.id === id)),
    [pinnedIds, unis]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return unis;
    return unis.filter((u) => u.name.toLowerCase().includes(q));
  }, [query, unis]);
  const visible = showAll ? filtered : filtered.slice(0, 12);

  // Show the uni's true city label whenever it's not just this city's name —
  // helps clarify Greater Manchester / LA metro / Bay Area cluster inclusions.
  const isMetroCity = (uniCity) => {
    if (!uniCity) return false;
    const base = uniCity.split(",")[0].split("(")[0].trim().toLowerCase();
    return base !== cityName.toLowerCase();
  };

  const handleTogglePin = (uni, evt) => {
    // The uni card is a <Link>; stop the row-navigation when clicking the pin.
    evt.preventDefault();
    evt.stopPropagation();
    const alreadyPinned = hasPinned(uni.id);
    if (!alreadyPinned && full) {
      toast.error("Compare shortlist is full (4 max). Remove one to add.");
      return;
    }
    togglePin(uni.id);
    toast.success(alreadyPinned
      ? `${uni.name} removed from compare`
      : `${uni.name} pinned — pick 1-3 more, then click Compare`);
  };

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="city-universities">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-editorial text-2xl">
          All {unis.length} universities in {cityName}
          {unis.some((u) => isMetroCity(u.city)) && (
            <span className="ml-1 text-xs text-stone-500 font-sans"> &amp; metro area</span>
          )}
        </h2>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by name…"
          data-testid="city-universities-search"
          className="text-sm px-3 py-1.5 border border-stone-300 rounded-md bg-white focus:outline-none focus:border-[#002147] w-64 max-w-full"
        />
      </div>

      {/* Compare action strip — visible only when the student has pinned
          at least one uni. Hitting Compare navigates to the shared
          /compare?u1..u4 side-by-side page. */}
      {pinnedInThisCity.length > 0 && (
        <div className="mb-4 bg-violet-50 border border-violet-200 rounded-lg px-4 py-2.5 flex items-center justify-between flex-wrap gap-3"
             data-testid="city-universities-compare-strip">
          <div className="text-sm text-stone-800">
            <Pin className="h-3.5 w-3.5 inline mr-1.5 text-[#7C3AED]" />
            {pinnedInThisCity.length} pinned
            <span className="text-xs text-stone-500 ml-1.5">
              · pin {pinnedInThisCity.length < 2 ? "1 more to compare" : "up to 4 for side-by-side"}
            </span>
          </div>
          <div className="flex gap-2">
            {pinnedInThisCity.length >= 2 && compareHref && (
              <Link
                to={compareHref}
                data-testid="city-universities-compare-btn"
                className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#7C3AED] text-white hover:bg-[#6D28D9]"
              >
                Compare {pinnedInThisCity.length} →
              </Link>
            )}
            <button
              onClick={() => { pinnedInThisCity.forEach((id) => togglePin(id)); }}
              data-testid="city-universities-clear-pins"
              className="text-xs text-stone-600 hover:text-red-600 px-2 py-1.5"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {filtered.length === 0 && (
        <div className="text-sm text-stone-500 bg-white border border-stone-200 rounded-lg p-6" data-testid="city-universities-empty">
          No universities matching "{query}".
        </div>
      )}

      {filtered.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {visible.map((u) => {
            const isFeatured = featuredIds.has(u.id);
            const metro = isMetroCity(u.city);
            const pinned = hasPinned(u.id);
            return (
              <Link
                key={u.id} to={`/uni/${u.id}`}
                data-testid={`city-uni-${u.id}`}
                className={`bg-white border rounded-lg p-4 hover:shadow-md transition-all flex items-center gap-3 ${
                  isFeatured ? "border-[#002147] shadow-sm" : "border-stone-200 hover:border-[#002147]"
                } ${pinned ? "ring-2 ring-[#7C3AED] ring-offset-1" : ""}`}
              >
                {u.logo_url ? (
                  <img
                    src={u.logo_url} alt={u.name}
                    className="h-10 w-10 object-contain shrink-0"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="h-10 w-10 shrink-0 rounded bg-stone-100 flex items-center justify-center">
                    <GraduationCap className="h-5 w-5 text-stone-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{u.name}</div>
                  <div className="text-xs text-stone-500 flex items-center gap-2 mt-0.5">
                    {u.world_rank && <span className="font-mono">#{u.world_rank}</span>}
                    {metro && <span className="text-amber-700">· {u.city}</span>}
                    {!metro && u.campus_type && <span>· {u.campus_type}</span>}
                  </div>
                </div>
                {isFeatured && (
                  <Badge className="text-[9px] uppercase tracking-wider bg-[#002147] text-white shrink-0">
                    Editor's pick
                  </Badge>
                )}
                <button
                  type="button"
                  onClick={(e) => handleTogglePin(u, e)}
                  data-testid={`city-uni-pin-${u.id}`}
                  title={pinned ? "Remove from compare" : "Pin to compare"}
                  className={`shrink-0 h-8 w-8 rounded-md flex items-center justify-center transition ${
                    pinned
                      ? "bg-[#7C3AED] text-white hover:bg-[#6D28D9]"
                      : "text-stone-400 hover:text-[#7C3AED] hover:bg-violet-50"
                  }`}
                >
                  {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                </button>
              </Link>
            );
          })}
        </div>
      )}

      {filtered.length > 12 && !showAll && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowAll(true)}
            data-testid="city-universities-show-all"
            className="text-sm text-[#002147] font-medium hover:underline"
          >
            Show all {filtered.length} →
          </button>
        </div>
      )}
      {showAll && filtered.length > 12 && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowAll(false)}
            data-testid="city-universities-show-less"
            className="text-sm text-stone-500 hover:text-[#002147] hover:underline"
          >
            Show less
          </button>
        </div>
      )}
    </section>
  );
}

// ============================================================================
// Sticky Table of Contents (2026-03-03).
// Desktop: fixed right rail (visible on xl+ screens only).
// Uses IntersectionObserver for scroll-spy — active section highlights as
// you scroll. Click-to-scroll on each item.
// ============================================================================

const _TOC_ITEMS = [
  ["city-cost-grid",             "Cost of living"],
  ["city-lifestyle-calculator",  "Lifestyle calculator"],
  ["city-friendliness",          "Student-friendliness"],
  ["city-research-scores",       "Research scores"],
  ["city-neighbourhoods",        "Neighbourhoods"],
  ["city-hangouts",              "Where students hang out"],
  ["city-venues",                "Student venues"],
  ["city-quotes",                "Student quotes"],
  ["city-find-students",         "Find students before arrival"],
  ["city-safety",                "Safety pulse"],
  ["city-arrival",               "First 72 hours"],
  ["city-weather",               "Weather"],
  ["city-faith",                 "Faith & community"],
  ["city-food",                  "Food from home"],
  ["city-getting-home",          "Getting home"],
  ["city-work",                  "Part-time work"],
  ["city-post-study-visa",       "Post-study visa"],
  ["city-events",                "Annual events"],
  ["city-wellbeing",             "Health & wellbeing"],
  ["city-universities",          "All universities"],
];

function CityTOC({ city }) {
  const [active, setActive] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = useMemo(() => {
    const has = {
      "city-cost-grid": !!city.cost_of_living,
      "city-lifestyle-calculator": !!city.cost_of_living,
      "city-friendliness": !!city.student_friendliness,
      "city-research-scores": true,
      "city-neighbourhoods": (city.neighbourhoods || []).length > 0,
      "city-hangouts": (city.hangouts || []).length > 0,
      "city-venues": true,
      "city-quotes": (city.student_quotes || []).length > 0,
      "city-find-students": (city.find_students_before_arrival || []).length > 0,
      "city-safety": !!city.safety,
      "city-arrival": !!city.arrival_checklist,
      "city-weather": !!city.weather,
      "city-faith": !!city.faith,
      "city-food": !!city.food_from_home,
      "city-getting-home": !!city.getting_home,
      "city-work": !!city.part_time_work,
      "city-post-study-visa": !!city.post_study_visa,
      "city-events": (city.annual_events || []).length > 0,
      "city-wellbeing": !!city.wellbeing,
      "city-universities": (city.universities_in_city || []).length > 0,
    };
    return _TOC_ITEMS.filter(([id]) => has[id]);
  }, [city]);

  useEffect(() => {
    if (!items.length) return;
    const nodes = items
      .map(([id]) => document.querySelector(`[data-testid='${id}']`))
      .filter(Boolean);
    if (!nodes.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActive(visible[0].target.getAttribute("data-testid"));
        }
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 },
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [items]);

  const scrollTo = (id) => {
    const el = document.querySelector(`[data-testid='${id}']`);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top: y, behavior: "smooth" });
    setMobileOpen(false);
  };

  if (!items.length) return null;

  return (
    <>
      <nav
        className="hidden xl:block fixed right-6 top-24 z-30 w-56 max-h-[calc(100vh-8rem)] overflow-y-auto rounded-2xl bg-white/35 backdrop-blur-md ring-1 ring-stone-200/60 px-3 py-3 pr-2 shadow-[0_1px_0_rgba(0,0,0,0.02),0_20px_40px_-24px_rgba(0,33,71,0.18)]"
        aria-label="Section navigation"
        data-testid="city-toc"
      >
        <div className="text-[10px] uppercase tracking-wider text-stone-400 mb-2 px-2">On this page</div>
        <ul className="space-y-0.5 text-xs">
          {items.map(([id, label]) => {
            const isActive = active === id;
            return (
              <li key={id}>
                <button
                  onClick={() => scrollTo(id)}
                  data-testid={`toc-${id}`}
                  className={`w-full text-left px-2 py-1.5 rounded transition-colors border-l-2 ${
                    isActive
                      ? "text-[#002147] font-semibold border-[#002147] bg-white"
                      : "text-stone-500 border-transparent hover:text-[#002147] hover:border-stone-300"
                  }`}
                >
                  {label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <button
        onClick={() => setMobileOpen((s) => !s)}
        data-testid="city-toc-mobile-toggle"
        className="xl:hidden fixed bottom-4 right-4 z-40 bg-[#002147] text-white rounded-full h-11 w-11 shadow-lg flex items-center justify-center hover:bg-[#001430]"
        aria-label="Open section menu"
      >
        <ClipboardList className="h-5 w-5" />
      </button>
      {mobileOpen && (
        <div
          className="xl:hidden fixed inset-0 z-[60] bg-black/40 flex items-end"
          onClick={() => setMobileOpen(false)}
          data-testid="city-toc-mobile-sheet"
        >
          <div
            className="bg-white w-full rounded-t-2xl p-5 max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-editorial text-lg">Jump to section</div>
              <button onClick={() => setMobileOpen(false)} className="text-stone-500 hover:text-stone-900 text-lg" aria-label="Close">×</button>
            </div>
            <ul className="space-y-1">
              {items.map(([id, label]) => (
                <li key={id}>
                  <button
                    onClick={() => scrollTo(id)}
                    className="w-full text-left px-3 py-2 rounded hover:bg-stone-100 text-sm text-stone-800"
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

